// S2 (v0.13.0) — super admin: create tenant form.
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateTenant } from "@/api/adminTenants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PLANS = ["basic", "professional", "enterprise", "banking"] as const;

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function TenantCreatePage() {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [plan, setPlan] = useState<(typeof PLANS)[number]>("basic");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useCreateTenant();
  const nav = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        name: name.trim(),
        slug: slug.trim() || slugify(name),
        plan,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      nav("/admin/tenants");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      if (typeof detail === "string" && detail.includes("slug")) {
        setError("This slug is already taken");
      } else {
        setError(typeof detail === "string" ? detail : "Failed to create tenant");
      }
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Create Tenant</h1>
        <p className="text-sm text-muted-foreground">
          New tenants start in <strong>active</strong> status with an empty
          settings object.
        </p>
      </div>
      <Card>
        <CardContent className="p-4">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="name">Tenant Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugDirty) setSlug(slugify(e.target.value));
                }}
                required
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  setSlugDirty(true);
                }}
                required
              />
              <p className="mt-1 text-xs text-muted-foreground">
                URL-safe, lowercase, hyphenated. Must be unique.
              </p>
            </div>
            <div>
              <Label htmlFor="plan">Plan</Label>
              <select
                id="plan"
                value={plan}
                onChange={(e) => setPlan(e.target.value as (typeof PLANS)[number])}
                className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
              >
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="expires_at">Expires (optional)</Label>
              <Input
                id="expires_at"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
            {error && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => nav("/admin/tenants")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                Create
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
